import { IStyle } from 'Styles';
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import Header from 'components/header/Header';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import TabButton from './TabButton';
import {
  EImagingTabOptions,
  getImagingSetupPath,
  imagingSetupTabs,
  imagingTabs,
} from './constants';
import { ImageSettingsRowInfo, ImagingTabPanel, ScheduleRowInfo } from './types';
import { useNavigate, useLocation } from 'react-router-dom';
import ImagingScheduling from './imagingScheduling';
import {
  useImagingSettings,
  useImagingSchedules,
  usePlateInspectionsToScore,
  usePlatesToImage,
  useRobots,
  fetchImageManagementTasks,
  fetchImagerQuota,
} from './hooks';
import {
  ImagingSchedulesDeletedDataDTO,
  SaveImageTaskDTO,
  SaveImagingSchedulesDTO,
} from 'business/models/Models';
import ImagingScheduleService from 'business/services/ImagingScheduleService';
import { showAlertSnackBar, showSnackBar } from 'utils/Global';
import {
  prepareImageSettingsDTOs,
  validateImageSettings,
  validateSchedule,
} from './utils';
import { RMModalProps } from 'components/modal/RMModal';
import { useDispatch } from 'react-redux';
import { setImagingSchedulesIsDirty } from 'redux/reducers/imagingSchedulesReducer';
import {
  setSelectedImagingSettingsID,
  setSelectedImagingSettingsRowNum,
} from 'redux/reducers/imagingSettingsReducer';
import {
  useImagingSchedulesIsDirty,
  useImagingSettingsID,
  useImagingSettingsRowNum,
} from 'redux/hooks/imagingSetup';
import ImageSettings from './ImageSettings/ImageSettingsContainer';
import CaptureProfileService from 'business/services/CaptureProfileService';
import ImageManagementTask from './ImageManagementTask';
import {
  setDisplayResults,
  setImageManagementTaskList,
  setImageManagementTasksIsDirty,
  setProgress,
} from 'redux/reducers/imageManagementTaskReducer';
import {
  useCheckedExplorerNodes,
  useDeletedImageActionIds,
  useImageManagementTaskIsDirty,
  useOriginalImageManagementTasks,
  useSelectedImageManagementTask,
} from 'redux/hooks/imageManagementTask';
import { cloneDeep } from 'lodash';
import {
  useOnSaveImageTasks,
  useOnSetSelectedImageManagementTaskId,
} from './ImageManagementTask/hooks';
import {
  deserializeImageTaskDTO,
  deserializeImagerQuotaDTO,
  showLargerTaskNameMessage,
  TASK_NAME_MAXIMUM_LENGTH,
} from './ImageManagementTask/utils';
import { ActionOperationValue } from './ImageManagementTask/constants';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import ImagerQuota from './ImagerQuota';
import {
  onUpdateImagerQuotaDirty,
  useImagerQuota,
  useImagerQuotaIsDirty,
  useOnSaveImagerQuota,
  useOriginalImagerQuota,
} from 'redux/hooks/imagerQuota';
import { setImagerQuotaList } from 'redux/reducers/imagerQuotaReducer';
import { useModalHandlers } from 'components/modal/hooks';
import { KeyboardKeys } from 'constants/';
import { useSaveDiscardButtons } from 'CustomHooks/useSaveDiscardButtons';

interface ImagingDashboardProps {
  setModal?: React.Dispatch<React.SetStateAction<RMModalProps>>;
}

const imagingScheduleService = new ImagingScheduleService();
function ImagingDashboard(props: ImagingDashboardProps) {
  const { setModal } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState<number>(EImagingTabOptions.Scheduling);
  const dispatch = useDispatch();
  const [tuningFieldError, setTuningFieldError] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [scheduleErrors, setScheduleErrors] = useState<ScheduleRowInfo>({});
  const [imageSettingErrors, setImageSettingErrors] = useState<ImageSettingsRowInfo>({});
  const [deletedData, setDeletedData] = useState<ImagingSchedulesDeletedDataDTO>();
  const isDirty = useImagingSchedulesIsDirty();
  const setIsDirty = (flag) => {
    dispatch(setImagingSchedulesIsDirty(flag));
  };
  const imageManagementTaskIsDirty = useImageManagementTaskIsDirty();
  const imagerQuotaIsDirty = useImagerQuotaIsDirty();
  const setImagerQuotaDirty = onUpdateImagerQuotaDirty();
  const setImageManagementTaskIsDirty = (flag) => {
    dispatch(setImageManagementTasksIsDirty(flag));
  };
  const selectedCps = useImagingSettingsID();
  const setSelectedCps = (value) => {
    dispatch(setSelectedImagingSettingsID(value));
  };
  const selectedRows = useImagingSettingsRowNum();
  const setSelectedRows = (value) => {
    dispatch(setSelectedImagingSettingsRowNum(value));
  };

  const {
    imagingSchedulesDto,
    setImagingSchedulesDto,
    imagingSchedulesOriginal,
    refetchImagingSchedules,
  } = useImagingSchedules();
  const { platesToImage, platesToImageOriginal, setPlatesToImage, refetchPlatesToImage } =
    usePlatesToImage();
  const {
    plateInspectionsToScore,
    plateInspectionsToScoreOriginal,
    setPlateInspectionsToScore,
    refetchPlateInspectionsToScore,
  } = usePlateInspectionsToScore();
  fetchImageManagementTasks();
  fetchImagerQuota();
  const refetchHandler = () => {
    refetchImagingSchedules();
    refetchPlatesToImage();
    refetchPlateInspectionsToScore();
  };
  const robots = useRobots();
  const onSaveImageTasks = useOnSaveImageTasks();
  const onSaveImagerQuota = useOnSaveImagerQuota();
  const deletedActionIds = useDeletedImageActionIds();
  const selectedTask = useSelectedImageManagementTask();
  const imageOriginalTasks = useOriginalImageManagementTasks();
  const imagerQuotaList = useImagerQuota();
  const imagerQuotaOriginalsList = useOriginalImagerQuota();
  const [enableAll, setEnableAll] = useState(false);
  const [importFlAll, setImportFlAll] = useState(false);
  const [deletedImagingSettings, setDeletedImagingSettings] = useState([]);
  const {
    originalImgSettings,
    currentImgSettings,
    setCurrentImgSettings,
    refetchImagingSettings,
  } = useImagingSettings();
  const { showModal, hideModal } = useModalHandlers();
  const setSelectedImageTaskId = useOnSetSelectedImageManagementTaskId();
  const previousTabRef = useRef<EImagingTabOptions | null>(null);
  const checkedExplorerNodes = useCheckedExplorerNodes();

  const saveImageSettings = async () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);

    try {
      const captureProfileService = new CaptureProfileService();

      const saveCaptureProfilesDTO = prepareImageSettingsDTOs(
        originalImgSettings,
        currentImgSettings,
        deletedImagingSettings,
      );

      const imageSettingsRowInfo: ImageSettingsRowInfo = {};
      saveCaptureProfilesDTO.captureProfilesDto.forEach((saveProfileDTO, _idx) => {
        const cpErrors = validateImageSettings(
          saveProfileDTO,
          saveCaptureProfilesDTO.captureProfilesDto,
          _idx,
          robots,
        );
        Object.assign(imageSettingsRowInfo, cpErrors);
      });

      if (Object.keys(imageSettingsRowInfo).length) {
        setImageSettingErrors(imageSettingsRowInfo);
        return;
      }

      setImageSettingErrors({});

      await captureProfileService.saveCaptureProfile(saveCaptureProfilesDTO);

      dispatch(setImagingSchedulesIsDirty(false));
      showSnackBar('Imaging Settings saved.');
      refetchImagingSettings();
    } finally {
      setIsSaving(false);
    }
  };

  const onSave = () => {
    if (tabIndex === EImagingTabOptions.Scheduling) {
      const scheduleRowInfo: ScheduleRowInfo = {};
      imagingSchedulesDto.forEach((schedule, _idx) => {
        const scheduleErrors = validateSchedule(schedule, imagingSchedulesDto, _idx);
        Object.assign(scheduleRowInfo, scheduleErrors);
      });
      if (Object.keys(scheduleRowInfo).length) {
        setScheduleErrors(scheduleRowInfo);
        const message = [
          ...new Set(
            Object.values(scheduleRowInfo).flatMap((row) =>
              `${row.msg}${Object.values(row.entryErrors ?? {})
                .map((entry) => entry.msg)
                .join('')}`
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean),
            ),
          ),
        ].join(' ');
        showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, message);
        return;
      } else {
        setScheduleErrors({});
      }
      const saveDto = new SaveImagingSchedulesDTO();
      saveDto.imagingSchedules = imagingSchedulesDto;
      saveDto.deletedData = deletedData;
      imagingScheduleService.saveImagingSchedules(saveDto).then(() => {
        dispatch(setImagingSchedulesIsDirty(false));
        refetchHandler();
        showSnackBar('Imaging Schedules saved.');
        setDeletedData(undefined);
      });
    } else if (tabIndex === EImagingTabOptions.ImagingSettings) {
      saveImageSettings();
    } else if (tabIndex === EImagingTabOptions.ImageManagementTask) {
      if (!selectedTask) {
        return;
      }
      dispatch(setDisplayResults([]));
      dispatch(setProgress(0));
      const saveImageTaskDTO = new SaveImageTaskDTO();
      saveImageTaskDTO.task = deserializeImageTaskDTO(selectedTask);
      saveImageTaskDTO.deletedActionIds =
        (deletedActionIds?.filter((a) => a > 0) as number[]) || [];
      if (saveImageTaskDTO.task.taskName.length > TASK_NAME_MAXIMUM_LENGTH) {
        showLargerTaskNameMessage(showModal, hideModal, saveImageTaskDTO.task.taskName);
        return;
      }

      const isNullOrNotNullOperator = (operator: any): boolean => {
        const op = operator?.toString().trim().toUpperCase() ?? '';
        return op === 'IS NULL' || op === 'IS NOT NULL';
      };

      const constraintsHaveMissingRuleValues = (constraints: any): boolean => {
        const rootRules = constraints?.rules;
        if (!Array.isArray(rootRules) || rootRules.length === 0) {
          return false;
        }

        const leafNodes: any[] = [];

        const collectLeafNodes = (nodes: any[] | undefined) => {
          if (!nodes) {
            return;
          }

          for (const node of nodes) {
            if (!node) {
              continue;
            }

            if (Array.isArray(node.rules)) {
              collectLeafNodes(node.rules as any);
              continue;
            }

            leafNodes.push(node);
          }
        };

        collectLeafNodes(rootRules as any);

        if (leafNodes.length === 0) {
          return true;
        }

        return leafNodes.some((leaf) => {
          const operator = leaf.operator?.toString().trim().toUpperCase() ?? '';
          if (!operator) {
            return true;
          }

          if (isNullOrNotNullOperator(operator) || leaf.isDropdown) {
            return false;
          }

          const value = leaf.value?.toString().trim() ?? '';
          return value.length === 0;
        });
      };

      for (const action of saveImageTaskDTO.task.imageManagementActionList ?? []) {
        const rootRuleCount = action.constraints?.rules?.length ?? 0;

        if (rootRuleCount === 0) {
          if (action.actionType === ActionOperationValue.Delete) {
            if (
              !action.excludeFirstInspection &&
              !action.excludeLastInspection &&
              action.minImageAge == null
            ) {
              showAlertSnackBar(
                EAlertType.ERROR,
                ESnackBarPosition.TOP,
                ' Delete actions must contain at least one search constraint.',
              );
              return;
            }
          }

          continue;
        }

        if (constraintsHaveMissingRuleValues(action.constraints)) {
          showAlertSnackBar(
            EAlertType.ERROR,
            ESnackBarPosition.TOP,
            'Unable to save with empty filter query.',
          );
          return;
        }
      }
      if (
        saveImageTaskDTO.task.imageManagementActionList.some(
          (action) =>
            action.actionType !== ActionOperationValue.Delete &&
            action.sourceImageStoreId === action.targetImageStoreId,
        )
      ) {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Source and target image stores must be different.',
        );
        return;
      }
      if (!saveImageTaskDTO.task.taskName || saveImageTaskDTO.task.taskName === '') {
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Task name cannot be empty.',
        );
        return;
      }
      if (saveImageTaskDTO.task && checkedExplorerNodes) {
        saveImageTaskDTO.task.explorerNodeIds = checkedExplorerNodes;
        saveImageTaskDTO.task.isDirty = true;
      }
      onSaveImageTasks(saveImageTaskDTO);
    } else if (tabIndex === EImagingTabOptions.ImagerQuota) {
      const imagerQuotaListDTO = deserializeImagerQuotaDTO(imagerQuotaList);
      onSaveImagerQuota(imagerQuotaListDTO);
    }
  };

  const onDiscard = () => {
    if (tabIndex === EImagingTabOptions.ImageManagementTask) {
      onDiscardImageManagementTask();
      return;
    }
    setImagingSchedulesDto(imagingSchedulesOriginal);
    setPlatesToImage(platesToImageOriginal);
    setPlateInspectionsToScore(plateInspectionsToScoreOriginal);
    setDeletedData(undefined);
    setScheduleErrors({});
    setImageSettingErrors({});
    setTuningFieldError(undefined);
    setCurrentImgSettings(cloneDeep(originalImgSettings));
    dispatch(setImagingSchedulesIsDirty(false));
    setImagerQuotaDirty(false);
    dispatch(setImagerQuotaList([...cloneDeep(imagerQuotaOriginalsList)]));
    setDeletedImagingSettings([]);
    dispatch(setDisplayResults([]));
    dispatch(setProgress(0));
  };

  const onDiscardImageManagementTask = () => {
    setImageManagementTaskIsDirty(false);
    dispatch(setImageManagementTaskList([...cloneDeep(imageOriginalTasks)]));
    setSelectedImageTaskId(0);
    dispatch(setDisplayResults([]));
    dispatch(setProgress(0));
  };

  const enableSaveDiscardButtons = () => {
    if (tabIndex === EImagingTabOptions.ImageManagementTask) {
      return selectedTask && imageManagementTaskIsDirty;
    } else if (tabIndex === EImagingTabOptions.ImagerQuota) {
      return imagerQuotaIsDirty;
    } else {
      return isDirty;
    }
  };

  const saveDiscardButtons = useSaveDiscardButtons({
    enableButtons: enableSaveDiscardButtons(),
    onSave: onSave,
    onDiscard: onDiscard,
  });

  const tabPanels: ImagingTabPanel[] = [
    {
      content: (
        <ImagingScheduling
          imagingSchedulesDto={imagingSchedulesDto}
          setImagingScheduleDto={setImagingSchedulesDto}
          setDeletedData={setDeletedData}
          scheduleErrors={scheduleErrors}
          platesToImage={platesToImage}
          plateInspectionsToScore={plateInspectionsToScore}
          setPlatesToImage={setPlatesToImage}
          setPlateInspectionsToScore={setPlateInspectionsToScore}
          setModal={setModal}
        />
      ),
    },
    {
      content: (
        <ImageSettings
          setEnableHeaderButtons={setIsDirty}
          cpData={currentImgSettings}
          setCpData={setCurrentImgSettings}
          enableAll={enableAll}
          setEnableAll={setEnableAll}
          importFlAll={importFlAll}
          setImportFlAll={setImportFlAll}
          imageSettingErrors={imageSettingErrors}
          selectedCps={selectedCps}
          setSelectedCps={setSelectedCps}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          deletedImagingSettings={deletedImagingSettings}
          setDeletedImagingSettings={setDeletedImagingSettings}
          robots={robots}
          tuningFieldError={tuningFieldError}
          setTuningFieldError={setTuningFieldError}
        />
      ),
    },
    {
      content: <ImageManagementTask />,
    },
    {
      content: <ImagerQuota />,
    },
  ];

  const changeTab = async (index: number) => {
    navigate(getImagingSetupPath(imagingSetupTabs[index].key));
  };

  useEffect(() => {
    const pathArray = location.pathname.split('/');
    const newTabIndex =
      imagingSetupTabs.find((tab) => tab.value === pathArray[pathArray.length - 1])
        ?.key ?? EImagingTabOptions.Scheduling;
    setTabIndex(newTabIndex);
    previousTabRef.current = newTabIndex;
    return () => {
      if (isDirty) {
        onDiscard();
      }
      // Discard IMT only if it's dirty
      if (imageManagementTaskIsDirty) {
        onDiscardImageManagementTask();
      }
    };
  }, [location.pathname]);

  const handleUserKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const ctrlKey = e.ctrlKey || e.metaKey;

      if (ctrlKey && e.key === KeyboardKeys.S) {
        e.preventDefault();
        if (enableSaveDiscardButtons()) {
          onSave();
        }
      }
    },
    [enableSaveDiscardButtons, onSave],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    window.addEventListener('keydown', handleUserKeyPress, { signal });

    return () => {
      abortController.abort();
    };
  }, [handleUserKeyPress]);
  return (
    <div id="imagingdashboard-wrapper">
      <div id="imagingdashboard-topbar" className="setup-bg-topbar">
        <Header
          containerStyle={styles.header}
          headerButtons={saveDiscardButtons}
          isNewTheme={true}
          title={'Imaging'}
        />
      </div>
      <div id="imagingdashboard-container" className="imaging-container">
        <Tabs
          id="imagingdashboard-tabs-wrapper"
          selectedIndex={tabIndex}
          onSelect={(index) => {
            if (index === tabIndex) {
              return false;
            }
            changeTab(index);
            // Prevent react-tabs from managing its own internal state;
            // we fully control the active tab via selectedIndex from route
            return false;
          }}
        >
          <TabList
            id="imagingdashboard-tablists"
            className="list-none flex p-0 m-0 items-center h-10"
            style={{
              borderBottom: '2px solid #eeeeee',
              marginBottom: '-2px !important',
            }}
          >
            {imagingTabs.map((tab, i) => (
              <Tab key={`imagingTab${i}`}>
                <TabButton title={tab.title} active={tabIndex === tab.tabType} />
              </Tab>
            ))}
          </TabList>
          <div id="imagingdashboard-tabpanels" className="w-full h-full bg-white">
            {tabPanels.map((panel, i) => (
              <TabPanel key={`pan${i}`}>
                <div
                  id={`imagingdashboard-tabpanel-panel${i}`}
                  className="flex flex-col w-full h-full"
                >
                  {panel.content}
                </div>
              </TabPanel>
            ))}
          </div>
        </Tabs>
      </div>
    </div>
  );
}

const styles: IStyle = {
  header: {
    marginTop: -6,
  },
  inUseLabel: {
    fontWeight: 400,
    marginLeft: '0.75rem',
  },
};

export default ImagingDashboard;
